import React, { useState, useEffect, useRef } from 'react'
import { Pencil, Check, X } from 'lucide-react'
import { useDirtyForm } from '@/contexts/DirtyFormContext'

interface DirtyKeyDescriptor {
  table: string
  id:    string
  field: string
}

interface Props {
  value:        string | null
  onSave?:      (v: string | null) => Promise<void>
  placeholder?: string
  multiline?:   boolean
  className?:   string
  emptyText?:   string
  disabled?:    boolean
  // when set, participates in DirtyFormContext: edits are staged into
  // the page-scoped batch instead of calling onSave directly. not under
  // a DirtyFormProvider, it transparently falls back to direct save (so
  // modals and legacy pages keep working)
  dirtyKey?:    DirtyKeyDescriptor
}

export const EditableText: React.FC<Props> = ({ value, onSave, placeholder, multiline, className, emptyText, disabled, dirtyKey }) => {
  const dirty = useDirtyForm()
  const usingDirty = dirtyKey !== undefined && dirty.isUnderDirtyForm
  const draftEntry = usingDirty ? dirty.getDraft(dirtyKey!.table, dirtyKey!.id, dirtyKey!.field) : undefined
  const displayValue: string | null = usingDirty
    ? (draftEntry ? (draftEntry.draftValue as string | null) : value)
    : value

  const [editing, setEditing] = useState(false)
  const [draft,   setDraft]   = useState(displayValue ?? '')
  const [saving,  setSaving]  = useState(false)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)

  // reset the input draft whenever displayValue changes from outside
  // (parent refetch, batch save flushed, sibling edit). only sync when
  // not actively editing, otherwise typing gets clobbered by every
  // parent re-render.
  useEffect(() => {
    if (!editing) setDraft(displayValue ?? '')
  }, [displayValue, editing])
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus() }, [editing])

  const commit = async () => {
    if (saving) return
    const trimmed = draft.trim()
    const next = trimmed === '' ? null : trimmed
    if (next === value) {
      // no change vs canonical value. clear any stale draft in the
      // context (from a prior edit) so the bar count drops.
      if (usingDirty) dirty.clearDraft(dirtyKey!.table, dirtyKey!.id, dirtyKey!.field)
      setEditing(false)
      return
    }
    if (usingDirty) {
      dirty.setDraft(dirtyKey!.table, dirtyKey!.id, dirtyKey!.field, value, next)
      setEditing(false)
      return
    }
    if (!onSave) { setEditing(false); return }
    setSaving(true)
    try {
      await onSave(next)
      setEditing(false)
    } catch {
      // toast handled by caller
    } finally {
      setSaving(false)
    }
  }

  const cancel = () => { setDraft(displayValue ?? ''); setEditing(false) }

  if (!editing) {
    const isDirty = usingDirty && draftEntry !== undefined
    if (disabled) {
      return displayValue ? (
        <span className={`${multiline ? 'whitespace-pre-wrap' : ''} ${className ?? ''}`}>{displayValue}</span>
      ) : (
        <span className={`text-slate-300 dark:text-slate-600 italic ${className ?? ''}`}>{emptyText ?? '—'}</span>
      )
    }
    return (
      <div className={`group inline-flex items-start gap-1.5 cursor-text ${isDirty ? 'ring-1 ring-amber-300 dark:ring-amber-600 rounded px-1' : ''} ${className ?? ''}`}
           onClick={() => setEditing(true)}>
        {displayValue ? (
          <span className={multiline ? 'whitespace-pre-wrap' : ''}>{displayValue}</span>
        ) : (
          <span className="text-slate-300 dark:text-slate-600 italic">{emptyText ?? 'Click to add'}</span>
        )}
        <Pencil className="h-2.5 w-2.5 text-slate-300 opacity-0 group-hover:opacity-100 mt-0.5 flex-shrink-0" />
      </div>
    )
  }

  return (
    <div className="inline-flex items-start gap-1 w-full">
      {multiline ? (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') cancel()
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit()
          }}
          placeholder={placeholder}
          rows={3}
          className={`flex-1 text-xs border border-blue-300 dark:border-blue-600 rounded px-2 py-1 bg-white dark:bg-slate-800 focus:outline-none focus:border-blue-500 ${className ?? ''}`}
          disabled={saving}
        />
      ) : (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') cancel()
            if (e.key === 'Enter') commit()
          }}
          placeholder={placeholder}
          className={`flex-1 text-xs border border-blue-300 dark:border-blue-600 rounded px-2 py-0.5 bg-white dark:bg-slate-800 focus:outline-none focus:border-blue-500 ${className ?? ''}`}
          disabled={saving}
        />
      )}
      <button onClick={commit} disabled={saving} className="p-0.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded">
        <Check className="h-3 w-3" />
      </button>
      <button onClick={cancel} disabled={saving} className="p-0.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
        <X className="h-3 w-3" />
      </button>
    </div>
  )
}
